import { LOCAL_STORAGE, LOGIN_CONTEXT } from "@constants/index";
import { createContext, useEffect, useState } from "react";

export const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(() => {
    const savedPermissions = localStorage.getItem(LOCAL_STORAGE.PERMISSIONS);
    return savedPermissions ? JSON.parse(savedPermissions) : [];
  });
  const [localeDefault, setLocaleDefault] = useState(null);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE.PERMISSIONS, JSON.stringify(permissions));
  }, [permissions]);

  const updatePermissions = (newPermissions) => {
    setPermissions(newPermissions);
  };

  const updateLocaleDefault = (localeDefault) => {
    setLocaleDefault(localeDefault);
  };

  const clearPermissions = () => {
    localStorage.removeItem(LOCAL_STORAGE.PERMISSIONS);
    setPermissions([]);
    setLocaleDefault(null);
  };

  const hasSuperUserPermission = permissions.includes(LOGIN_CONTEXT.SUPERUSER);
  const hasManagerPermission = permissions.includes(LOGIN_CONTEXT.MANAGER);
  const hasProductCreatorPermission = permissions.includes(LOGIN_CONTEXT.PRODUCT_CREATOR);
  const hasProductManagerPermission = permissions.includes(LOGIN_CONTEXT.PRODUCT_MANAGER);
  const hasProjectManagerPermission = permissions.includes(LOGIN_CONTEXT.PROJECT_MANAGER);
  const hasDeveloperPermission = permissions.includes(LOGIN_CONTEXT.DEVELOPER);
  const hasLicensePolicyEditPermission = permissions.includes(LOGIN_CONTEXT.LICENSE_POLICY_EDIT);
  const hasSbomEditPermission = permissions.includes(LOGIN_CONTEXT.SBOM_EDIT);
  const hasVulnerabilityPolicyEditPermission = permissions.includes(LOGIN_CONTEXT.VULNERABILITY_POLICY_EDIT);

  const hasManagePermission = hasSuperUserPermission || hasManagerPermission;
  const hasExtendedManagePermission  = hasManagePermission || hasProductCreatorPermission || hasProductManagerPermission || hasProjectManagerPermission;
  // SUPERUSER | MANAGER | PROJECT_MANAGER — use for SBOM workflow management UI (e.g. WorkflowProgressSbom).
  const hasProjectManagementPermission = hasManagePermission || hasProjectManagerPermission;

  const isCurrentUser = (userId) => {
    const loggedInUserId = localStorage.getItem(LOCAL_STORAGE.USER_ID);
    if (userId == null || userId === "" || !loggedInUserId) return false;
    return String(loggedInUserId) === String(userId);
  };

  /**
   * Project-scoped workflow permissions (Clarity/SBOM workflow UIs).
   * Requirement: user must have role-level project management permission AND be the Project Manager of that project.
   * If projectManagerId is not provided, fall back to role-level permission only.
   */
  const canManageProjectWorkflow = (projectManagerId) => {
    if (!hasProjectManagementPermission) return false;
    if (hasManagePermission) return true;
    if (projectManagerId == null || projectManagerId === "") return false;
    return isCurrentUser(projectManagerId);
  };

  /**
   * VEX evaluate / import / approve.
   * SUPERUSER | MANAGER | PROJECT_MANAGER of that project (same scope as workflow).
   */
  const canEditVex = (projectManagerId) => canManageProjectWorkflow(projectManagerId);

  // User management permissions
  // Requirement: everyone can add, edit, delete user except Developer.
  const canAddUserPermission = !hasDeveloperPermission;
  const canDeleteUserPermission = !hasDeveloperPermission;

  // Product management permissions
  // Requirement: ProductCreator can add and delete, but can not edit.
  // Requirement: ProductManager only edit.
  const canAddProductPermission = hasProductCreatorPermission || hasManagePermission;
  const canEditProductPermission = hasProductManagerPermission || hasManagePermission;
  const canDeleteProductPermission = hasProductCreatorPermission || hasManagePermission;

  // Add-project flow: role-level only (no project scope yet). Project detail uses useProjectDetailPermissions.
  const canAddProjectMemberPermission = hasManagePermission;

  const canEditSbom = () => { 
    return hasSuperUserPermission || hasSbomEditPermission;
  };

  const canEditVulnerabilityPolicy = () => {
    return hasSuperUserPermission || hasVulnerabilityPolicyEditPermission;
  };

  const canView = (routePermissions) => {
    if (!routePermissions || !routePermissions.view) return false;
    return permissions.some(perm => routePermissions.view.includes(perm));
  }

  const canEdit = (routePermissions) => {
    if (!routePermissions || !routePermissions.edit) return false;
    return permissions.some(perm => routePermissions.edit.includes(perm));
  };

  const canAdd = (routePermissions) => {
    if (!routePermissions || !routePermissions.add) return false;
    return permissions.some(perm => routePermissions.add.includes(perm));
  };

  const canDelete = (routePermissions) => {
    if (!routePermissions || !routePermissions.delete) return false;
    return permissions.some(perm => routePermissions.delete.includes(perm));
  };

  // Use helper variables
  const shouldHideSystemMenu = !permissions?.length ||
    (!hasExtendedManagePermission && !hasSbomEditPermission);

  const shouldShowOnlySbomInSystem = permissions?.length > 0 &&
    hasSbomEditPermission && !hasManagePermission;

  // ============================================
  // Comment Permission Functions
  // ============================================
  
  // Case 1: ON_VERIFICATION + Usage Type Check + Admin role
  const canAddCommentOnUsageTypeCheck = () => {
    return hasProjectManagementPermission;
  };

  // Case 2: ON_REVIEW + Admin role
  const canAddCommentOnReview = () => {
    return hasProjectManagementPermission;
  };

  // Case 3: STANDBY_FOLLOWUP + All users
  const canAddCommentOnStandbyFollowup = () => {
    return true;
  };

  // Case 4: ON_VERIFICATION + Usage Type Review + All users
  const canAddCommentOnUsageTypeReview = () => {
    return true;
  };

  // Case 5: ON_VERIFICATION + Usage Type Input + All users
  const canAddCommentOnUsageTypeInput = () => {
    return true;
  };

  // Case 6: FOLLOWUP_CHECK + All users
  const canAddCommentOnFollowupCheck = () => {
    return hasProjectManagementPermission;
  };

  // Case 7: FOLLOWUP_CHECK + All users
  const canAddCommentOnAnalysisCheck = () => {
    return true;
  };

  // Main function to check if commenting is allowed for current step
  const canAddCommentOnStep = (verificationStatusCd, verificationStatusCdName) => {
    if (!verificationStatusCd) return false;

    // Case 1: ON_VERIFICATION + Usage Type Check + Admin role
    if (verificationStatusCd === "ON_VERIFICATION" && verificationStatusCdName === "Usage Type Check") {
      return canAddCommentOnUsageTypeCheck();
    }

    // Case 2: ON_REVIEW + Admin role
    if (verificationStatusCd === "ON_REVIEW") {
      return canAddCommentOnReview();
    }

    // Case 3: STANDBY_FOLLOWUP + All users
    if (verificationStatusCd === "STANDBY_FOLLOWUP") {
      return canAddCommentOnStandbyFollowup();
    }

    // Case 4: ON_VERIFICATION + Usage Type Review + All users
    if (verificationStatusCd === "ON_VERIFICATION" && verificationStatusCdName === "Usage Type Review") {
      return canAddCommentOnUsageTypeReview();
    }

    // Case 5: ON_VERIFICATION + Usage Type Input + All users
    if (verificationStatusCd === "ON_VERIFICATION" && verificationStatusCdName === "Usage Type Input") {
      return canAddCommentOnUsageTypeInput();
    }

    // Case 6: FOLLOWUP_CHECK + All users
    if (verificationStatusCd === "FOLLOWUP_CHECK") {
      return canAddCommentOnFollowupCheck();
    }

    // Case 7: ANALYSIS_CHECK + All users
    if (verificationStatusCd === "ANALYSIS_CHECK") {
      return canAddCommentOnAnalysisCheck();
    }
    return false;
  };

  // ============================================
  // Request Action Permission Functions
  // ============================================

  // Check if user can manage approvals
  const canManageApprovals = () => {
    return hasProjectManagementPermission;
  };

  // Check if user can complete a request
  // Parameters: isAssignee (boolean), isRequester (boolean)
  const canCompleteRequest = (isAssignee, isRequester) => {
    return hasSuperUserPermission || hasProjectManagerPermission || isAssignee || isRequester;
  };

  // Check if user can see action buttons for a request
  // Parameters: actionStatus (string), isAssignee (boolean), isRequester (boolean)
  const getRequestActionButtons = (actionStatus, isAssignee, isRequester) => {
    // For PENDING_COMPLETION: Show Complete button if user can complete
    if (actionStatus === "PENDING COMPLETION") {
      return canCompleteRequest(isAssignee, isRequester) ? ["complete"] : [];
    }
    
    // For PENDING_APPROVAL: Show Reject/Approve buttons if user can manage approvals
    if (actionStatus === "PENDING APPROVAL") {
      // Developers cannot approve/reject
      if (hasDeveloperPermission) return [];
      // Only superuser/managers can approve/reject
      if (canManageApprovals()) return ["reject", "approve"];
      return [];
    }
    
    return [];
  };

  return (
    <PermissionContext.Provider
      value={{
        hasSuperUserPermission,
        hasDeveloperPermission,
        hasManagerPermission,
        hasLicensePolicyEditPermission,
        hasSbomEditPermission,
        hasVulnerabilityPolicyEditPermission,
        canEditSbom,
        canEditVulnerabilityPolicy,
        permissions,
        updatePermissions,
        clearPermissions,
        localeDefault,
        updateLocaleDefault,
        canView,
        canEdit,
        canAdd,
        canDelete,
        hasManagePermission,
        hasExtendedManagePermission,
        hasProjectManagementPermission,
        canManageProjectWorkflow,
        canEditVex,
        shouldHideSystemMenu,
        shouldShowOnlySbomInSystem,
        // Comment permission functions
        canAddCommentOnStep,
        // Request action permission functions
        getRequestActionButtons,
        //User permission functions
        canAddUserPermission,
        canDeleteUserPermission,
        // Product permission functions
        canAddProductPermission,
        canEditProductPermission,
        canDeleteProductPermission,
        canAddProjectMemberPermission,
        hasProductManagerPermission,
        hasProjectManagerPermission,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};
